'use client';

import { Button } from '@/shared/shadcn/ui/button';
import { useSuperForm } from '@/shared/super/hooks/use-super-form';
import { SuperForm, InputSuperFormField } from '@/shared/super/ui/super-form';
import { FormMessage } from '@/shared/shadcn/ui/form';
import { useEffect, useState } from 'react';
import { requestCode } from '../api/request-code.api';
import { verifyCode } from '../api/verify-code';
import { toast } from 'sonner';
import { type ApiError } from 'next/dist/server/api-utils';
import { useRouter } from 'next/navigation';
import { type Control } from 'react-hook-form';

export function SignInForm() {
  const router = useRouter();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [timer, setTimer] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval!);
            setIsTimerActive(false);
            return 60;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerActive]);

  const form = useSuperForm({
    schema: (z) =>
      step === 'email'
        ? {
            email: z.email('Введите корректное значение почты'),
          }
        : {
            email: z.email('Введите корректное значение почты'),
            code: z.string('Введите код, отправленный на почту'),
          },

    onSubmit: async ({ email, code }: { email: string; code: string }) => {
      if (step === 'email') {
        try {
          await requestCode(email);

          setStep('code');
          setIsTimerActive(true);
        } catch (e) {
          toast((e as ApiError).message);
        }
      } else {
        try {
          await verifyCode(email, code);

          router.push('/articles');
        } catch (e) {
          toast((e as ApiError).message);
        }
      }
    },
  });

  const handleResendCode = async () => {
    if (isTimerActive) return;

    const email = form.getValues('email');

    if (!email) {
      toast('Email не найден.');
      return;
    }

    try {
      await requestCode(email);
      setIsTimerActive(true);
      setTimer(60);
      toast('Новый код отправлен.');
    } catch (e) {
      toast((e as ApiError).message);
    }
  };

  // Control<RubricFormData> is not directly assignable to Control<any>: TypeScript enforces
  // contravariance in ValidateForm<T> parameter types, where Path<RubricFormData> (a literal
  // union) is incompatible with Path<any>=string. Casting through FieldValues resolves this
  // because Path<FieldValues>=string matches the expected string type in Control<any>.
  const control = form.control as unknown as Control;

  return (
    <SuperForm {...form} onSubmit={form.onSubmit}>
      {step === 'code' ? (
        <div className="flex flex-col gap-5">
          <p className="text-center text-sm text-gray-500">Код действует в течение 5 минут.</p>
          <InputSuperFormField key="code" control={control} name="code" placeholder="01234" />
          <Button type="button" variant="secondary" onClick={() => void handleResendCode()} disabled={isTimerActive}>
            {isTimerActive ? `Отправить код еще раз (${timer}с)` : 'Отправить код еще раз'}
          </Button>
        </div>
      ) : (
        <InputSuperFormField key="email" control={control} name="email" placeholder="Email" />
      )}
      <Button type="submit" className="w-full">
        {step === 'email' ? 'Запросить код' : 'Войти'}
      </Button>
      <FormMessage />
    </SuperForm>
  );
}
